import { delay } from '@theme/global-utilities';

/**
 * GraphQLClient – minimal resilient GraphQL fetcher for Shopify Storefront API
 * ---------------------------------------------------------------------------
 * Responsibilities:
 *  - Send POST requests to Storefront API with query + variables
 *  - Handle timeouts, retries, and 429 "Too Many Requests"
 *  - Return a consistent result shape { ok, data, error }
 */

/**
 * @template T
 * @typedef {object} GraphQLResponse
 * @property {boolean} ok - True if request succeeded and no GraphQL errors.
 * @property {T|null} data - Parsed GraphQL data (or null on failure).
 * @property {Error|null} error - Error object if failed, else null.
 */
export class GraphQLClient {
  /**
   * @param {object} opts
   * @param {string} opts.endpoint - Full Storefront GraphQL endpoint URL.
   * @param {string} opts.token - Storefront API access token.
   * @param {number} [opts.timeoutMs=15000] - Request timeout in ms.
   * @param {number} [opts.maxRetries=3] - Number of retry attempts on network/HTTP errors.
   */
  constructor({ endpoint, token, timeoutMs = 15000, maxRetries = 3 }) {
    this.endpoint = endpoint;
    this.token = token;
    this.timeoutMs = timeoutMs;
    this.maxRetries = maxRetries;
  }

  /**
   * Execute a GraphQL query with variables.
   *
   * @template T
   * @param {string} query - GraphQL query string.
   * @param {object} [variables={}] - Variables for the query.
   * @returns {Promise<GraphQLResponse<T>>}
   */
  async fetch(query, variables = {}) {
    let attempt = 0;

    while (true) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeoutMs);

      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.token,
          },
          body: JSON.stringify({ query, variables }),
          signal: controller.signal,
        });

        clearTimeout(timer);

        // Handle 429 rate limit
        if (res.status === 429 && attempt < this.maxRetries) {
          const retryAfter =
            Number(res.headers.get('Retry-After')) * 1000 ||
            this._backoff(attempt);
          await delay(retryAfter);
          attempt++;
          continue;
        }

        const payload = await res.json().catch(() => ({}));
        const { data, errors } = payload || {};

        if (!res.ok || errors) {
          const message = errors
            ? /** @type {{ message: string }[]} */ (errors)
                .map(({ message }) => message)
                .join(', ')
            : `HTTP ${res.status}`;
          if (attempt < this.maxRetries) {
            await delay(this._backoff(attempt));
            attempt++;
            continue;
          }
          return { ok: false, data: null, error: new Error(message) };
        }

        return { ok: true, data, error: null };
      } catch (err) {
        clearTimeout(timer);
        if (attempt < this.maxRetries) {
          await delay(this._backoff(attempt));
          attempt++;
          continue;
        }
        return {
          ok: false,
          data: null,
          error: err instanceof Error ? err : new Error(String(err)),
        };
      }
    }
  }

  /**
   * Compute exponential backoff delay in milliseconds.
   *
   * @private
   * @param {number} attempt - Retry attempt index (0-based).
   * @param {number} [base=500] - Base delay in ms for the first attempt.
   * @returns {number} Milliseconds to wait before next retry.
   */
  _backoff(attempt, base = 500) {
    // Exponential backoff with cap (0.5s - ~32s)
    return base * 2 ** Math.min(attempt, 6);
  }
}

/**
 * createStorefrontClient
 * Minimal factory for a Storefront GraphQL client.
 *
 * Auto-detects endpoint/token unless you override.
 *
 * @param {object} [opts]
 * @param {string} [opts.apiVersion='2025-04']
 * @param {number} [opts.timeoutMs=15000]
 * @param {number} [opts.maxRetries=3]
 * @param {string} [opts.endpoint]
 * @param {string} [opts.token]
 * @returns {GraphQLClient}
 */
export const createStorefrontClient = function createStorefrontClient({
  apiVersion = '2025-04',
  timeoutMs = 15000,
  maxRetries = 3,
  endpoint,
  token,
} = {}) {
  const shop =
    (typeof Shopify !== 'undefined' && Shopify.shop) ||
    (window.Shopify && window.Shopify.shop);

  const resolvedEndpoint =
    endpoint || `https://${shop}/api/${apiVersion}/graphql.json`;
  const resolvedToken =
    token || window.shopifyVariables.STOREFRONT_ACCESS_TOKEN;

  if (!resolvedEndpoint || !resolvedToken) {
    throw new Error('[createStorefrontClient] Missing endpoint or token');
  }

  return new GraphQLClient({
    endpoint: resolvedEndpoint,
    token: resolvedToken,
    timeoutMs,
    maxRetries,
  });
};
